import { useCallback, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  BrandBookingsOpenDocument,
  IBrandBookingsOpenQuery,
  useBrandBookingsOpenQuery,
} from '../services/brandRulesService/__generated__/queries.generated';

export interface BookingsOpenState {
  open: boolean;
  /** The server's text for a guest who presses Pay; null while bookings are open. */
  message: string | null;
}

/** Spec 023: an API without the fields, or a failed request, reads as open (the server still refuses). */
export function bookingsOpenFrom(
  data: IBrandBookingsOpenQuery | null | undefined,
): BookingsOpenState {
  const rules = data?.myBrandRules;
  if (rules?.bookingsOpen !== false) {
    return { open: true, message: null };
  }
  return { open: false, message: rules.bookingsClosedMessage?.trim() || null };
}

export interface UseBookingsOpen extends BookingsOpenState {
  /** Asks the server again (not the cache), for the moment a guest presses Pay. */
  check: () => Promise<BookingsOpenState>;
}

/**
 * Spec 023: whether this site takes bookings yet. A brand that is not operating
 * still shows cars and checkout; checkout calls `check()` before the card is sent.
 */
export function useBookingsOpen(): UseBookingsOpen {
  const client = useApolloClient();
  const { data } = useBrandBookingsOpenQuery({ fetchPolicy: 'cache-first', errorPolicy: 'ignore' });

  const check = useCallback(async () => {
    try {
      const result = await client.query({
        query: BrandBookingsOpenDocument,
        fetchPolicy: 'network-only',
        errorPolicy: 'ignore',
      });
      return bookingsOpenFrom(result.data);
    } catch {
      return { open: true, message: null };
    }
  }, [client]);

  return useMemo(() => ({ ...bookingsOpenFrom(data), check }), [data, check]);
}

export interface BookingsClosedPrompt {
  /** The dialog's text; null while it is closed. An empty string shows the fallback. */
  message: string | null;
  show: (message: string) => void;
  close: () => void;
  /** For a card form's `beforeConfirm`: false (and the dialog) while the site is closed. */
  beforeConfirm: () => Promise<boolean>;
}

/**
 * Spec 023: the "Bookings open soon" dialog of a checkout. `beforeConfirm` asks the
 * server when the guest presses Pay; `show` opens it for a server refusal
 * (`BRAND_NOT_ACCEPTING_BOOKINGS`) that reached an older path.
 */
export function useBookingsClosedPrompt(): BookingsClosedPrompt {
  const { check } = useBookingsOpen();
  const [message, setMessage] = useState<string | null>(null);
  const beforeConfirm = useCallback(async () => {
    const state = await check();
    if (state.open) return true;
    setMessage(state.message ?? '');
    return false;
  }, [check]);
  return useMemo(
    () => ({
      message,
      show: (text: string) => setMessage(text),
      close: () => setMessage(null),
      beforeConfirm,
    }),
    [message, beforeConfirm],
  );
}
